import { useQuery } from "@tanstack/react-query";
import { programmeDashboardApi } from "../../programmeDashboard/api/programmeDashboard";
import { useLiveProgressRisksMutation } from "../mutations";

export interface RiskTickerRisk {
  id: string;
  title: string;
  status: string;
  type: string;
  impact: "high" | "medium" | "low";
  probability: "high" | "medium" | "low";
}

const mapImpactScoreToLevel = (score: number): "high" | "medium" | "low" => {
  if (score >= 4) return "high";
  if (score >= 2) return "medium";
  return "low";
};

const mapProbabilityToLevel = (
  probability: string
): "high" | "medium" | "low" => {
  if (!probability) return "low";
  const lower = probability.toLowerCase();
  if (lower.includes("high")) return "high";
  if (lower.includes("medium")) return "medium";
  return "low";
};

const mapApiRiskToRiskTickerRisk = (apiRisk: {
  risk_id: string;
  title: string;
  category: string;
  status: string;
  impact_score: number;
  probability: string;
}): RiskTickerRisk => {
  return {
    id: apiRisk.risk_id,
    title: apiRisk.title,
    status: apiRisk.status,
    type: apiRisk.category,
    impact: mapImpactScoreToLevel(apiRisk.impact_score),
    probability: mapProbabilityToLevel(apiRisk.probability),
  };
};

export const useLiveProgressRisks = ({ is_construction }: { is_construction?: string }) => {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["live-progress-risks", is_construction],
    queryFn: () =>
      programmeDashboardApi.fetchRisks({
        page: 1,
        limit: 100,
        sort_field: "risk_id",
        sort_order: "DESC",
        status: "open",
        is_construction: is_construction
      }),
  });

  const mutation = useLiveProgressRisksMutation();

  const risks: RiskTickerRisk[] = data?.data?.rows
    ? data.data.rows.map(mapApiRiskToRiskTickerRisk)
    : [];

  return {
    risks,
    isLoading: isLoading || mutation.isPending,
    isError,
    error: error instanceof Error ? error.message : "Failed to fetch risks",
    refetch: mutation.mutate,
  };
};
